import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Inbox, Lock, User, Clock, MessageSquare, Send, UserCheck, UserMinus, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = '/api';

const PRIORITY_META = {
  low: { label: 'Nízká', color: 'bg-slate-100 text-slate-800' },
  medium: { label: 'Střední', color: 'bg-blue-100 text-blue-800' },
  high: { label: 'Vysoká', color: 'bg-red-100 text-red-800' },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

export default function ITRequestsPage() {
  const { user, isAdmin, canManageIt } = useAuth();
  const canManage = canManageIt;
  const [requests, setRequests] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, m] = await Promise.all([
        axios.get(`${API}/it/requests`, { withCredentials: true }),
        axios.get(`${API}/it/statuses`, { withCredentials: true }),
        axios.get(`${API}/it/managers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRequests(r.data || []);
      setStatuses(s.data || []);
      setManagers(m.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (canManage) load(); else setLoading(false); }, [canManage]);

  const statusMap = useMemo(() => Object.fromEntries(statuses.map(s => [s.id, s])), [statuses]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter(r => r.status_id === statusFilter);
  }, [requests, statusFilter]);

  const changeStatus = async (reqId, statusId) => {
    try {
      await axios.patch(`${API}/it/requests/${reqId}`, { status_id: statusId }, { withCredentials: true });
      toast.success('Status změněn');
      load();
      if (detail?.id === reqId) refreshDetail(reqId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  const changeAssignee = async (reqId, assigneeId) => {
    try {
      await axios.patch(`${API}/it/requests/${reqId}`, { assignee_id: assigneeId || null }, { withCredentials: true });
      toast.success('Přiděleno');
      load();
      if (detail?.id === reqId) refreshDetail(reqId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  const takeRequest = async (reqId) => {
    try {
      const { data } = await axios.post(`${API}/it/requests/${reqId}/take`, {}, { withCredentials: true });
      toast.success(`Převzato: ${data.owner_name}`);
      load();
      if (detail?.id === reqId) refreshDetail(reqId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  const releaseRequest = async (reqId) => {
    try {
      await axios.post(`${API}/it/requests/${reqId}/release`, {}, { withCredentials: true });
      toast.success('Uvolněno');
      load();
      if (detail?.id === reqId) refreshDetail(reqId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  const refreshDetail = async (reqId) => {
    try {
      const { data } = await axios.get(`${API}/it/requests/${reqId}`, { withCredentials: true });
      setDetail(data);
    } catch (e) { /* noop */ }
  };

  const addNote = async () => {
    if (!noteText.trim() || !detail) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/it/requests/${detail.id}/notes`, { text: noteText }, { withCredentials: true });
      setNoteText('');
      refreshDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    } finally {
      setSavingNote(false);
    }
  };

  if (!canManage) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="bg-white border-[#E2E8F0] shadow-sm max-w-md w-full">
            <CardContent className="py-16 text-center">
              <Lock className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#052E16] font-['Manrope'] mb-2">Přístup odepřen</h2>
              <p className="text-[#57534E]">Tato stránka je jen pro IT správce. Kontaktujte administrátora.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) return <Layout><div className="p-12 text-center text-[#57534E]">Načítám...</div></Layout>;

  const counts = requests.reduce((acc, r) => {
    acc.total++;
    acc[r.status_id] = (acc[r.status_id] || 0) + 1;
    return acc;
  }, { total: 0 });

  return (
    <Layout>
      <div className="space-y-4" data-testid="it-requests-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-3">
              <Inbox className="w-8 h-8 text-[#16A34A]" /> IT — Požadavky
            </h1>
            <p className="text-[#57534E] mt-1">Správa všech IT servisních požadavků ({counts.total})</p>
          </div>
          <Button onClick={load} variant="outline" data-testid="it-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Obnovit
          </Button>
        </div>

        {/* Status filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            data-testid="filter-all"
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              statusFilter === 'all' ? 'bg-[#052E16] text-white border-[#052E16]' : 'bg-white text-[#57534E] border-[#E2E8F0]'
            }`}
          >
            Vše ({counts.total})
          </button>
          {statuses.map(s => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              data-testid={`filter-${s.id}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                statusFilter === s.id ? 'text-white' : ''
              }`}
              style={statusFilter === s.id
                ? { backgroundColor: s.color, borderColor: s.color }
                : { backgroundColor: s.color + '20', color: s.color, borderColor: s.color + '55' }}
            >
              {s.name} ({counts[s.id] || 0})
            </button>
          ))}
        </div>

        {/* Table */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left py-2 px-4 font-semibold">Vytvořeno</th>
                    <th className="text-left py-2 px-4 font-semibold">Uživatel</th>
                    <th className="text-left py-2 px-4 font-semibold">Předmět</th>
                    <th className="text-left py-2 px-4 font-semibold">Priorita</th>
                    <th className="text-left py-2 px-4 font-semibold">Status</th>
                    <th className="text-left py-2 px-4 font-semibold">Vlastník</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const status = statusMap[r.status_id];
                    const pr = PRIORITY_META[r.priority] || PRIORITY_META.medium;
                    return (
                      <tr key={r.id} onClick={() => { setDetail(r); refreshDetail(r.id); }} className="border-b border-[#F1F5F9] hover:bg-[#F8FAF8] cursor-pointer" data-testid={`req-row-${r.id}`}>
                        <td className="py-2 px-4 text-xs text-[#57534E] whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                        <td className="py-2 px-4 text-[#1C1917]">{r.user_name}</td>
                        <td className="py-2 px-4 text-[#1C1917] font-medium max-w-[350px] truncate">{r.subject}</td>
                        <td className="py-2 px-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${pr.color}`}>{pr.label}</span>
                        </td>
                        <td className="py-2 px-4">
                          {status && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                                  style={{ backgroundColor: status.color + '20', color: status.color, borderColor: status.color + '55' }}>
                              {status.name}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-4 text-xs text-[#57534E]">
                          {r.owner_name || <span className="text-[#94A3B8] italic">volné</span>}
                        </td>
                        <td className="py-2 px-2 text-[#94A3B8]"><Inbox className="w-3.5 h-3.5" /></td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-sm text-[#94A3B8]">Žádné požadavky</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Detail */}
        <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setNoteText(''); } }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="it-detail-dialog">
            {detail && (() => {
              const status = statusMap[detail.status_id];
              const pr = PRIORITY_META[detail.priority] || PRIORITY_META.medium;
              const isMyOwned = detail.owner_id === String(user?._id);
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope'] text-[#052E16] flex items-start gap-2">
                      <span className="flex-1">{detail.subject}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${pr.color} shrink-0`}>{pr.label}</span>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-[#94A3B8]">Uživatel:</span> <strong className="text-[#1C1917]">{detail.user_name}</strong><br/><span className="text-[#94A3B8]">{detail.user_email}</span></div>
                      <div><span className="text-[#94A3B8]">Vytvořeno:</span> <strong className="text-[#1C1917]">{fmtDateTime(detail.created_at)}</strong></div>
                    </div>

                    <div className="p-3 rounded-lg bg-[#F8FAF8] border border-[#E2E8F0]">
                      <p className="text-xs text-[#94A3B8] uppercase font-bold mb-1">Popis</p>
                      <p className="text-sm text-[#1C1917] whitespace-pre-wrap">{detail.description}</p>
                    </div>

                    {/* Controls */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] uppercase text-[#94A3B8] font-bold">Status</label>
                        <select
                          value={detail.status_id || ''}
                          onChange={e => changeStatus(detail.id, e.target.value)}
                          data-testid="detail-status-select"
                          className="mt-1 w-full rounded-md border border-[#E2E8F0] px-2 py-1.5 text-sm"
                        >
                          {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-[#94A3B8] font-bold">Pracuje na tom</label>
                        <select
                          value={detail.assignee_id || ''}
                          onChange={e => changeAssignee(detail.id, e.target.value)}
                          data-testid="detail-assignee-select"
                          className="mt-1 w-full rounded-md border border-[#E2E8F0] px-2 py-1.5 text-sm"
                        >
                          <option value="">— nepřiděleno —</option>
                          {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-[#94A3B8] font-bold">Vlastník</label>
                        <div className="mt-1">
                          {detail.owner_id ? (
                            <div className="flex items-center gap-2">
                              <span className="flex-1 text-sm font-medium text-[#1C1917] truncate">{detail.owner_name}</span>
                              {(isMyOwned || isAdmin) && (
                                <Button size="sm" variant="outline" onClick={() => releaseRequest(detail.id)} data-testid="release-btn" className="h-7 px-2 border-red-300 text-red-600 hover:bg-red-50">
                                  <UserMinus className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button size="sm" onClick={() => takeRequest(detail.id)} data-testid="take-btn" className="w-full h-8 bg-[#16A34A] hover:bg-[#15803D] text-white">
                              <UserCheck className="w-3.5 h-3.5 mr-1" /> Převzít
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <p className="text-xs text-[#94A3B8] uppercase font-bold mb-2 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Interní poznámky ({detail.notes?.length || 0})</p>
                      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                        {(detail.notes || []).map(n => (
                          <div key={n.id} className="p-2.5 rounded-lg border border-[#E2E8F0] bg-white">
                            <div className="flex items-center justify-between text-[11px] text-[#94A3B8] mb-1">
                              <span className="font-semibold text-[#1C1917]"><User className="w-3 h-3 inline mr-1" />{n.user_name}</span>
                              <span>{fmtDateTime(n.created_at)}</span>
                            </div>
                            <p className="text-sm text-[#1C1917] whitespace-pre-wrap">{n.text}</p>
                          </div>
                        ))}
                        {(!detail.notes || detail.notes.length === 0) && <p className="text-xs text-[#94A3B8] italic text-center py-2">Žádné poznámky</p>}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <textarea
                          value={noteText}
                          onChange={e => setNoteText(e.target.value)}
                          rows={2}
                          placeholder="Přidat interní poznámku..."
                          data-testid="note-input"
                          className="flex-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 text-sm focus:border-[#16A34A] focus:outline-none focus:ring-1 focus:ring-[#16A34A]"
                        />
                        <Button onClick={addNote} disabled={savingNote || !noteText.trim()} data-testid="add-note-btn" className="bg-[#16A34A] hover:bg-[#15803D] text-white">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
